/**
 * Persistence utilities for game state
 * Handles localStorage operations with error handling
 */

import type { GameState } from '../types/game';

const STORAGE_KEY = 'playing-history-v1';

interface PersistedState {
  version: number;
  game: {
    xp: number;
    streak: number;
    bestStreak: number;
    totalQuestions: number;
    correctCount: number;
  };
  settings: {
    language: 'en' | 'lt' | 'be';
    botDifficulty: 'easy' | 'medium' | 'hard';
    soundEnabled: boolean;
  };
  /** In-progress game state (optional) */
  inProgress?: {
    deck: GameState['deck'];
    trump: GameState['trump'];
    trumpCard: GameState['trumpCard'];
    discard: GameState['discard'];
    playerHand: GameState['playerHand'];
    botHand: GameState['botHand'];
    playZone: GameState['playZone'];
    isPlayerAttacking: boolean;
    gamePhase: GameState['gamePhase'];
    answered: GameState['answered'];
    matchStartTime: number | null;
  };
}

const DEFAULT_STATE: PersistedState = {
  version: 1,
  game: {
    xp: 0,
    streak: 0,
    bestStreak: 0,
    totalQuestions: 0,
    correctCount: 0,
  },
  settings: {
    language: 'en',
    botDifficulty: 'medium',
    soundEnabled: true,
  },
};

/**
 * Load persisted state from localStorage
 */
export function loadPersistedState(): Partial<PersistedState> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw);

    // Version check for migrations
    if (parsed.version !== DEFAULT_STATE.version) {
      console.warn('State version mismatch, using defaults');
      return {};
    }

    return parsed;
  } catch (e) {
    console.error('Failed to load persisted state:', e);
    return {};
  }
}

/**
 * Save state to localStorage
 */
export function savePersistedState(state: PersistedState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('Failed to save persisted state:', e);
  }
}

/**
 * Clear persisted state
 */
export function clearPersistedState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.error('Failed to clear persisted state:', e);
  }
}

/**
 * Export all data (for backup/share)
 */
export function exportAllData(gameState: GameState, srsData: string): string {
  const exportData = {
    version: DEFAULT_STATE.version,
    exportedAt: new Date().toISOString(),
    game: {
      xp: gameState.xp,
      streak: gameState.streak,
      bestStreak: gameState.bestStreak,
      totalQuestions: gameState.totalQuestions,
      correctCount: gameState.correctCount,
      answered: gameState.answered,
    },
    srs: srsData,
  };

  return JSON.stringify(exportData, null, 2);
}

/**
 * Import data from backup
 */
export function importAllData(json: string): {
  success: boolean;
  error?: string;
  data?: {
    game: PersistedState['game'];
    srs: string;
  };
} {
  try {
    const parsed = JSON.parse(json);

    if (parsed.version !== DEFAULT_STATE.version) {
      return {
        success: false,
        error: `Version mismatch: expected ${DEFAULT_STATE.version}, got ${parsed.version}`,
      };
    }

    return {
      success: true,
      data: {
        game: parsed.game,
        srs: parsed.srs,
      },
    };
  } catch (e) {
    return {
      success: false,
      error: 'Invalid JSON format',
    };
  }
}

/**
 * Check if localStorage is available
 */
export function isStorageAvailable(): boolean {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Save in-progress game state
 */
export function saveGameProgress(state: PersistedState['inProgress']): void {
  try {
    const existing = loadPersistedState();
    const updated: PersistedState = {
      ...DEFAULT_STATE,
      ...existing,
      version: DEFAULT_STATE.version,
      inProgress: state,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (e) {
    console.error('Failed to save game progress:', e);
  }
}

/**
 * Load in-progress game state
 */
export function loadGameProgress(): PersistedState['inProgress'] | null {
  try {
    const state = loadPersistedState();
    return state.inProgress || null;
  } catch (e) {
    console.error('Failed to load game progress:', e);
    return null;
  }
}

/**
 * Clear in-progress game state
 */
export function clearGameProgress(): void {
  try {
    const existing = loadPersistedState();
    const updated: PersistedState = {
      ...DEFAULT_STATE,
      ...existing,
      version: DEFAULT_STATE.version,
      inProgress: undefined,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (e) {
    console.error('Failed to clear game progress:', e);
  }
}
